import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Skeleton,
  useToast,
} from '@org/ui';
import { Plus, Trash2, Users, Shield } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/admin/moderators')({
  component: AdminModerators,
});

function AdminModerators() {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [spaceOpen, setSpaceOpen] = useState(false);

  const utils = communityTrpc.useUtils();

  const { data, isLoading } = communityTrpc.admin.moderators.list.useQuery();
  const { data: spacesData } = communityTrpc.spaces.list.useQuery();
  const { data: searchData } = communityTrpc.admin.moderators.searchUsers.useQuery(
    { query: userSearch },
    { enabled: userSearch.length >= 2 }
  );

  const assignMutation = communityTrpc.admin.moderators.assign.useMutation({
    onSuccess: (result) => {
      utils.admin.moderators.list.invalidate();
      if (result.alreadyAssigned) {
        toast({ title: t('admin.moderators.alreadyAssigned') });
      } else {
        toast({ title: t('admin.moderators.assignSuccess') });
      }
      setSelectedUserId(null);
      setSelectedUserName('');
      setSpaceOpen(false);
    },
  });

  const unassignMutation = communityTrpc.admin.moderators.unassign.useMutation({
    onSuccess: () => {
      utils.admin.moderators.list.invalidate();
      toast({ title: t('admin.moderators.unassignSuccess') });
    },
  });

  const assignments = data?.assignments ?? [];
  const spaces = spacesData?.spaces ?? [];
  const searchResults = searchData?.users ?? [];

  const assignmentsBySpace = new Map<string, typeof assignments>();
  for (const a of assignments) {
    const list = assignmentsBySpace.get(a.spaceId) ?? [];
    list.push(a);
    assignmentsBySpace.set(a.spaceId, list);
  }

  function handleAssign(spaceId: string) {
    if (!selectedUserId) return;
    assignMutation.mutate({ userId: selectedUserId, spaceId });
  }

  function handleUnassign(userId: string, spaceId: string) {
    unassignMutation.mutate({ userId, spaceId });
  }

  function getUserName(user: { firstName: string | null; lastName: string | null; displayName: string | null }) {
    return user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || 'User';
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('admin.moderators.title')}</h1>
        <p className="text-muted-foreground">{t('admin.moderators.subtitle')}</p>
      </div>

      {/* Add moderator form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('admin.moderators.addModerator')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {/* User search */}
            <div className="flex-1">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal">
                    {selectedUserName || t('admin.moderators.selectUser')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('admin.moderators.selectUser')}
                      value={userSearch}
                      onValueChange={setUserSearch}
                    />
                    <CommandList>
                      <CommandEmpty>{t('admin.moderators.noUsersFound')}</CommandEmpty>
                      <CommandGroup>
                        {searchResults.map((user) => {
                          const name = getUserName(user);
                          return (
                            <CommandItem
                              key={user.userId}
                              value={user.userId}
                              onSelect={() => {
                                setSelectedUserId(user.userId);
                                setSelectedUserName(name);
                              }}
                              className="cursor-pointer"
                            >
                              <Avatar className="mr-2 h-6 w-6">
                                <AvatarImage src={user.profileImageUrl ?? undefined} />
                                <AvatarFallback className="text-xs">
                                  {name.slice(0, 1).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{name}</p>
                                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Space selector */}
            <div className="flex-1">
              <Popover open={spaceOpen} onOpenChange={setSpaceOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                    disabled={!selectedUserId}
                  >
                    {t('admin.moderators.selectSpace')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('admin.moderators.selectSpace')} />
                    <CommandList>
                      <CommandEmpty>{t('admin.moderators.noSpacesFound')}</CommandEmpty>
                      <CommandGroup>
                        {spaces.map((space) => (
                          <CommandItem
                            key={space.spaceId}
                            value={space.name}
                            onSelect={() => handleAssign(space.spaceId)}
                            className="cursor-pointer"
                          >
                            {space.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assignments list grouped by space */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" aria-hidden="true" />
            {t('admin.moderators.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : assignments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center">
              <Users className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="mt-2 text-muted-foreground">
                {t('admin.moderators.noAssignments')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('admin.moderators.noAssignmentsHint')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.from(assignmentsBySpace.entries()).map(([spaceId, spaceAssignments]) => (
                <div key={spaceId} className="rounded-lg border border-border p-4">
                  <h3 className="mb-3 font-semibold">{spaceAssignments[0].spaceName}</h3>
                  <div className="space-y-2">
                    {spaceAssignments.map((a) => {
                      const name = getUserName(a);
                      return (
                        <div
                          key={a.moderatorAssignmentId}
                          className="flex items-center gap-3 rounded-lg bg-muted/50 p-2"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={a.profileImageUrl ?? undefined} />
                            <AvatarFallback className="text-xs">
                              {name.slice(0, 1).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{name}</p>
                            <p className="truncate text-xs text-muted-foreground">{a.email}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassign(a.userId, a.spaceId)}
                            disabled={unassignMutation.isPending}
                            aria-label={t('admin.moderators.unassign')}
                            className="shrink-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
